import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from 'app/store';
import { ApiResListsType } from 'global-services/api/OhsApiModels';
import { getOhsLocalStorage, setOhsLocalStorage } from 'global-services/OhsDataParse';
import OhsCorrespondenceExchangeRecord from 'correspondence/models/OhsCorrespondenceExchangeRecord';
import { searchCorrespondenceExchangeLists } from 'correspondence/OhsCorrespondenceServices';

import { getMyCommunicationLists } from '../../OhsMyHubServices';
import { OhsMyCommunicationFilterPayload } from '../../models/OhsMyHubModel';

export interface MyCommListsRecordState {
  isLoading: boolean;
  myCommList: ApiResListsType<OhsCorrespondenceExchangeRecord[]> | null;
  currentPage: number;
}

const initialState: MyCommListsRecordState = {
  isLoading: false,
  myCommList: null,
  currentPage: 1,
};

const myCommunicationSearch = async (
  searchDetails: any
): Promise<ApiResListsType<OhsCorrespondenceExchangeRecord[]> | null> => {
  const globalSearchRes: any = await searchCorrespondenceExchangeLists(searchDetails);
  return globalSearchRes;
};

const exchangeFilterOptions = (state: RootState): Partial<OhsMyCommunicationFilterPayload> => {
  const { globalfilter, myCommunication } = state;
  const filter = globalfilter.filterInfo;

  const dateExchange = filter?.myCommunicationModule?.dateExchange;
  const hasValidDate = typeof dateExchange?.end === 'string' && dateExchange.end.trim() !== '';

  return {
    viewPreset: 'view_2',
    next: myCommunication.currentPage > 10 ? myCommunication.myCommList?.pagination.next ?? '' : '',
    archived: filter.myCommunicationModule.archived,
    status: filter.myCommunicationModule.status,
    ...(hasValidDate ? { dateExchange } : {}),
  };
};

const myCommListRPC = async (setExchangeFilters: OhsMyCommunicationFilterPayload): Promise<any> => {
  const response = await getMyCommunicationLists(setExchangeFilters);
  return response;
};

export const fetchMyCommunicationListsAsync = createAsyncThunk<
  ApiResListsType<OhsCorrespondenceExchangeRecord[]> | null,
  void,
  { state: RootState }
>('exchange/fetchMyCommunicationList', async (_, thunkAPI) => {
  const state = thunkAPI.getState();
  const filter = thunkAPI.getState().globalfilter.filterInfo;
  const searchFilter = thunkAPI.getState().globalSearch.searchInfo;
  const hasGlobalSearch = state.globalSearch.searchInfo.searchKey !== '';
  const setExchangeFilters: OhsMyCommunicationFilterPayload = {
    ...exchangeFilterOptions(state),
    page: state.myCommunication.currentPage > 10 ? 0 : state.myCommunication.currentPage,
    sort: { ...JSON.parse(String(filter.myCommunicationModule.sort)) },
    count: false,
  };

  try {
    const response = hasGlobalSearch
      ? await myCommunicationSearch({
          ...searchFilter,
          viewPreset: 'view_2',
          filter: {
            page: state.myCommunication.currentPage > 10 ? 0 : state.myCommunication.currentPage,
            count: false,
          },
          sort: { order: -1, key: 'dateCreated' },
        })
      : await myCommListRPC(setExchangeFilters);
    return response as ApiResListsType<OhsCorrespondenceExchangeRecord[]> | null;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchMyCommunicationListsCountAsync = createAsyncThunk<
  ApiResListsType<OhsCorrespondenceExchangeRecord[]> | null,
  void,
  { state: RootState }
>('exchange/fetchMyCommunicationCountList', async (_, thunkAPI) => {
  const state = thunkAPI.getState();
  const searchFilter = thunkAPI.getState().globalSearch.searchInfo;
  const hasGlobalSearch = state.globalSearch.searchInfo.searchKey !== '';
  const exchangeFiltersWithCount: OhsMyCommunicationFilterPayload = {
    ...exchangeFilterOptions(state),
    count: true,
    page: 1,
    sort: { order: -1, key: 'dateCreated' },
  };

  try {
    const response = hasGlobalSearch
      ? await myCommunicationSearch({
          ...searchFilter,
          viewPreset: 'view_2',
          filter: {
            count: true,
            page: 1,
          },
          sort: { order: -1, key: 'dateCreated' },
        })
      : await getMyCommunicationLists(exchangeFiltersWithCount);
    return response as ApiResListsType<OhsCorrespondenceExchangeRecord[]> | null;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const myCommSlice = createSlice({
  name: 'myCommunication',
  initialState,
  reducers: {
    setMyCommCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyCommunicationListsAsync.pending, (state) => {
        state.isLoading = true;
        state.myCommList = {
          items: [],
          pagination: state.myCommList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchMyCommunicationListsAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.myCommList = action.payload;
          if (state?.myCommList?.pagination?.totalPages) {
            setOhsLocalStorage('myCommListtotalPages', state.myCommList.pagination.totalPages);
          }
          const setItemsLastId = [...(state.myCommList?.items ?? [])].pop()?._id ?? '';
          state.myCommList.pagination.next = setItemsLastId;
          state.myCommList.pagination.totalPages = getOhsLocalStorage('myCommListtotalPages') ?? 0;
          state.isLoading = false;
        }
      })
      .addCase(fetchMyCommunicationListsAsync.rejected, (state) => {
        state.myCommList = null;
      })
      .addCase(fetchMyCommunicationListsCountAsync.fulfilled, (state, action) => {
        if (state.myCommList && action.payload && action.payload.pagination) {
          setOhsLocalStorage('myCommListtotalPages', action?.payload?.pagination?.totalPages);
          state.myCommList.pagination.totalPages = action?.payload?.pagination?.totalPages ?? 0;
        }
      });
  },
});

const myCommState = (state: RootState) => state.myCommunication;
export const getOhsMyCommunicationModuleState = createSelector(
  [myCommState],
  (myCommunication) => myCommunication
);
export const { setMyCommCurrentPage } = myCommSlice.actions;
export const myCommModuleReducer = myCommSlice.reducer;
